import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orgsApi, manualEntriesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { authApi } from '../services/api';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Building2, Save, Copy, Users, ShieldCheck, BadgeCheck,
  Heart, FileText, KeyRound, CalendarClock, Zap,
} from 'lucide-react';

import { reportsApi } from '../services/api';

export default function OrgSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState(null);
  const [stats, setStats] = useState(null);
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [auditEmail, setAuditEmail] = useState('');
  const [gpsEnabled, setGpsEnabled] = useState(true);
  const [autoBundleEnabled, setAutoBundleEnabled] = useState(false);
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [autoBundleLookback, setAutoBundleLookback] = useState(14);
  const [lastAutoRun, setLastAutoRun] = useState(null);
  const [runningNow, setRunningNow] = useState(false);

  const canEdit = user?.role === 'organisation';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [orgRes, statsRes] = await Promise.all([
          orgsApi.getMine(),
          orgsApi.myStats(),
        ]);
        if (cancelled) return;
        setOrg(orgRes.data);
        setStats(statsRes.data);
        setName(orgRes.data?.name || '');
        setContactEmail(orgRes.data?.contact_email || '');
        setAuditEmail(orgRes.data?.default_audit_email || '');
        setGpsEnabled(orgRes.data?.gps_tracking_enabled !== false);
        setAutoBundleEnabled(!!orgRes.data?.auto_bundle_enabled);
        setRemindersEnabled(orgRes.data?.approval_reminders_enabled !== false);
        setAutoBundleLookback(orgRes.data?.auto_bundle_lookback_days || 14);
        setLastAutoRun(orgRes.data?.last_auto_bundle_run_at || null);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load organisation');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await orgsApi.updateMine({
        name: name.trim(),
        contact_email: contactEmail.trim() || null,
        default_audit_email: auditEmail.trim() || null,
        gps_tracking_enabled: gpsEnabled,
        auto_bundle_enabled: autoBundleEnabled,
        auto_bundle_lookback_days: autoBundleLookback,
        approval_reminders_enabled: remindersEnabled,
      });
      setOrg(data);
      setLastAutoRun(data?.last_auto_bundle_run_at || lastAutoRun);
      toast.success('Organisation updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleRunAutoBundleNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await reportsApi.autoGenerateNow();
      setLastAutoRun(new Date().toISOString());
      toast.success(
        data?.bundles_created > 0
          ? `Created ${data.bundles_created} bundle${data.bundles_created === 1 ? '' : 's'} across ${data.orgs_processed} tenant${data.orgs_processed === 1 ? '' : 's'}`
          : 'No new reports since last run — nothing to bundle right now'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run auto-bundle');
    } finally {
      setRunningNow(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900"
            data-testid="org-settings-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="text-xs text-slate-500" data-testid="org-settings-id">
            Workspace ID: <span className="font-mono">{org?.id?.slice(0, 8)}</span>
            <button
              onClick={() => copy(org.id, 'Workspace ID')}
              className="ml-2 text-teal-600 hover:text-teal-700 align-middle"
              data-testid="org-settings-copy-id"
            >
              <Copy className="w-3 h-3 inline" />
            </button>
          </div>
        </div>

        <div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-teal-600" /> Organisation settings
          </h1>
          <p className="text-slate-600 mt-1">
            Manage your tenant&apos;s display name, contact email and view workspace stats.
          </p>
        </div>

        {/* Stats grid */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="org-settings-stats">
            <StatCard label="Members" value={stats.members} icon={Users} testid="stat-members" />
            <StatCard label="Workers" value={stats.workers} icon={ShieldCheck} testid="stat-workers" />
            <StatCard label="Participants" value={stats.participants} icon={Heart} testid="stat-participants" />
            <StatCard label="Reports" value={stats.reports} icon={FileText} testid="stat-reports" />
          </div>
        )}

        {/* Settings form */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <BadgeCheck className="w-5 h-5 text-teal-600" /> Workspace details
            </CardTitle>
            <CardDescription>
              {canEdit
                ? 'These details show on invoices, NDIS exports and email notifications sent to your team.'
                : 'Only organisation admins can edit these details. Ask your admin to update.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4" data-testid="org-settings-form">
              <div className="space-y-1">
                <Label htmlFor="org-name">Organisation name</Label>
                <Input
                  id="org-name"
                  data-testid="org-settings-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canEdit}
                  required
                  minLength={2}
                  maxLength={120}
                  className="h-11"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="org-email">Contact email</Label>
                <Input
                  id="org-email"
                  data-testid="org-settings-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  disabled={!canEdit}
                  placeholder="hello@example.com"
                  className="h-11"
                />
                <p className="text-xs text-slate-500">
                  Used for password reset and weekly digest recipient defaults.
                </p>
              </div>

              <div className="space-y-1">
                <Label htmlFor="audit-email">Default audit/compliance email</Label>
                <Input
                  id="audit-email"
                  data-testid="org-settings-audit-email"
                  type="email"
                  value={auditEmail}
                  onChange={(e) => setAuditEmail(e.target.value)}
                  disabled={!canEdit}
                  placeholder="auditor@example.com"
                  className="h-11"
                />
                <p className="text-xs text-slate-500">
                  Pre-filled when sending PDFs from the Reports page. Leave blank for none.
                </p>
              </div>

              <div className="rounded-lg border border-slate-200 p-3 flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <Label className="text-base">GPS travel tracking</Label>
                  <p className="text-xs text-slate-500 max-w-md">
                    When enabled, workers&apos; phones auto-record kilometres travelled
                    while clocked in. The total appears on their shift report so they
                    don&apos;t have to type it. Workers must grant location permission
                    on their device for tracking to start.
                  </p>
                </div>
                <Switch
                  checked={gpsEnabled}
                  onCheckedChange={setGpsEnabled}
                  disabled={!canEdit}
                  data-testid="org-settings-gps-toggle"
                />
              </div>

              <div className="flex items-start justify-between gap-4 border border-amber-100 bg-amber-50/30 rounded-lg p-4">
                <div className="space-y-1">
                  <Label className="text-base">Daily approval reminders</Label>
                  <p className="text-xs text-slate-500 max-w-md">
                    Every morning at 8&nbsp;AM Australia/Sydney, supervisors and admins get an in-app
                    notification (and email, if configured) listing manual timesheet entries still
                    awaiting approval — so nothing is missed before payroll closes.
                  </p>
                  <Button type="button" size="sm" variant="outline" className="mt-1 h-8 border-amber-300 text-amber-800 hover:bg-amber-50" disabled={sendingReminders}
                    onClick={async () => { setSendingReminders(true); try { const { data } = await manualEntriesApi.sendReminders(); toast.success(data.notified ? `Reminder sent to ${data.notified} supervisor${data.notified === 1 ? '' : 's'}` : 'Nothing pending — no reminder needed'); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSendingReminders(false); } }}
                    data-testid="org-settings-send-reminders">
                    {sendingReminders ? 'Sending…' : 'Send reminder now'}
                  </Button>
                </div>
                <Switch
                  checked={remindersEnabled}
                  onCheckedChange={setRemindersEnabled}
                  disabled={!canEdit}
                  data-testid="org-settings-reminders-toggle"
                />
              </div>

              <div className="border border-teal-100 bg-teal-50/30 rounded-lg p-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <Label className="text-base flex items-center gap-2">
                      <CalendarClock className="w-4 h-4 text-teal-600" />
                      Scheduled weekly bundles
                    </Label>
                    <p className="text-xs text-slate-500 max-w-md">
                      Every Sunday at 10&nbsp;PM Australia/Sydney, Visitex will auto-build a unified
                      bundle for every client with new reports and drop it in Saved Bundles — ready
                      for you to review, email, or attach an AI summary the next morning.
                    </p>
                  </div>
                  <Switch
                    checked={autoBundleEnabled}
                    onCheckedChange={setAutoBundleEnabled}
                    disabled={!canEdit}
                    data-testid="org-settings-auto-bundle-toggle"
                  />
                </div>
                {autoBundleEnabled && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-teal-100">
                    <div className="space-y-1">
                      <Label className="text-sm">Lookback window (days)</Label>
                      <Input
                        type="number"
                        min={7}
                        max={31}
                        value={autoBundleLookback}
                        onChange={(e) => setAutoBundleLookback(Math.min(31, Math.max(7, parseInt(e.target.value || '14', 10))))}
                        disabled={!canEdit}
                        className="h-10 max-w-[160px]"
                        data-testid="org-settings-auto-bundle-lookback"
                      />
                      <p className="text-xs text-slate-500">
                        How many days of reports go into each auto-bundle. 14 = fortnightly.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Last automatic run</Label>
                      <p className="text-sm text-slate-700 h-10 flex items-center">
                        {lastAutoRun
                          ? new Date(lastAutoRun).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', dateStyle: 'medium', timeStyle: 'short' })
                          : <span className="text-slate-400">Not yet run</span>}
                      </p>
                      {canEdit && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleRunAutoBundleNow}
                          disabled={runningNow}
                          className="border-teal-200 text-teal-700 hover:bg-teal-50"
                          data-testid="org-settings-run-auto-bundle-now"
                        >
                          {runningNow ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Running…</>
                          ) : (
                            <><Zap className="w-3 h-3 mr-1" /> Run now</>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <Label>URL slug</Label>
                <div className="flex items-center gap-2">
                  <Input value={org?.slug || ''} disabled className="h-11 font-mono bg-slate-100 text-slate-600" />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => copy(org.slug, 'Slug')}
                    className="h-11"
                    data-testid="org-settings-copy-slug"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Slugs are immutable to keep links and audit trails stable.</p>
              </div>

              <div className="space-y-1">
                <Label>Created</Label>
                <p className="text-sm text-slate-700">
                  {org?.created_at ? new Date(org.created_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', dateStyle: 'long', timeStyle: 'short' }) : '—'}
                  {org?.is_default && (
                    <span className="ml-2 inline-block bg-teal-100 text-teal-700 text-xs px-2 py-0.5 rounded">
                      Default Visitex tenant
                    </span>
                  )}
                </p>
              </div>

              {canEdit && (
                <div className="pt-2">
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="org-settings-save"
                  >
                    {saving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save changes</>
                    )}
                  </Button>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        <ChangePasswordCard />
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPw.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      await authApi.changePassword(currentPw, newPw);
      toast.success('Password changed successfully');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <KeyRound className="w-5 h-5 text-teal-600" /> Change my password
        </CardTitle>
        <CardDescription>
          Update the password for your own account. After saving you&apos;ll stay logged in on this device.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3 max-w-md" data-testid="change-password-form">
          <div className="space-y-1">
            <Label htmlFor="cur-pw">Current password</Label>
            <Input id="cur-pw" type="password" required minLength={1}
              value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
              data-testid="current-password" autoComplete="current-password" className="h-11" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-pw">New password (min 8 chars)</Label>
            <Input id="new-pw" type="password" required minLength={8}
              value={newPw} onChange={(e) => setNewPw(e.target.value)}
              data-testid="new-password" autoComplete="new-password" className="h-11" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="confirm-pw">Confirm new password</Label>
            <Input id="confirm-pw" type="password" required minLength={8}
              value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
              data-testid="confirm-password" autoComplete="new-password" className="h-11" />
          </div>
          <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700"
            data-testid="change-password-submit">
            {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</>) :
              (<><KeyRound className="w-4 h-4 mr-2" /> Update password</>)}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3"
      data-testid={testid}
    >
      <div className="w-9 h-9 rounded-md bg-teal-50 flex items-center justify-center text-teal-600">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-lg font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
}
